import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getFirestore,
  doc,
  getDocs,
  getDoc,
  collection,
  updateDoc,
  arrayUnion,
  setDoc,
  query,
  where,
  limit,
  startAfter,
  orderBy,
  collectionGroup
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
  CircularProgress,
  Skeleton
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import NavbarDashboard from '../Dashboard/NavbarDashboard';
import ResponsiveDrawer from '../Dashboard/Sidebar';
import {
  getCurrentFinancialYearValue,
  getFinancialYearOptions,
  isDateInFinancialYear,
} from '../../utils/financialYear';

const firestore = getFirestore();
const storage = getStorage();

// ─── Constants ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;
const POLLING_INTERVAL = 5 * 60 * 1000; // 5 min
const YEAR_OPTIONS = getFinancialYearOptions(2023, 2030).map(option => ({
  ...option,
  firestoreKey: `tdsData${option.value}`,
  filename: `tdsdoc-${option.value}.pdf`
}));

// ─── Parallel batch fetch — all 4 collections in ONE Promise.all ──────────────
const fetchAllCollections = async () => {
  const [userSnap, tdsStateSnap, tdsDetailsSnap, tdsFormSnap] = await Promise.all([
    getDocs(collection(firestore, 'users')),
    getDocs(collection(firestore, 'tds_state')),
    getDocs(collection(firestore, 'tds_details')),
    getDocs(collection(firestore, 'tds_forms'))
  ]);

  const tdsStateMap = new Map(tdsStateSnap.docs.map(d => [d.id, d.data()]));
  const tdsDetailsMap = new Map(tdsDetailsSnap.docs.map(d => [d.id, d.data()]));
  const tdsFormMap = new Map(
    tdsFormSnap.docs.map(d => {
      const data = d.data();
      return [d.id, { ...data, timestamp: data.timestamp?.toDate() ?? null }];
    })
  );

  const merged = userSnap.docs.map(d => {
    const id = d.id;
    const tdsFormData = tdsFormMap.get(id) ?? {};
    return {
      id,
      ...d.data(),
      tdsState:    tdsStateMap.get(id)   ?? { fileTds: false, tdsA26s: false },
      tdsDetails:  tdsDetailsMap.get(id) ?? {},
      tdsFormData
    };
  });

  return merged.sort((a, b) => {
    const ta = a.tdsFormData.timestamp ? +a.tdsFormData.timestamp : 0;
    const tb = b.tdsFormData.timestamp ? +b.tdsFormData.timestamp : 0;
    return tb - ta;
  });
};

// ─── Skeleton row for loading state ──────────────────────────────────────────
const SkeletonRow = () => (
  <TableRow>
    {Array.from({ length: 7 }).map((_, i) => (
      <TableCell key={i}><Skeleton variant="text" /></TableCell>
    ))}
  </TableRow>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const TdsDetails = ({ userPermissions }) => {
  const [userData, setUserData]                   = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [loadingMore, setLoadingMore]             = useState(false);
  const [selectedUser, setSelectedUser]           = useState(null);
  const [selectedFile, setSelectedFile]           = useState(null);
  const [dialogOpen, setDialogOpen]               = useState(false);
  const [additionalFields, setAdditionalFields]   = useState([{ company: '', income: '', tds: '' }]);
  const [searchTerm, setSearchTerm]               = useState('');
  const [debouncedSearch, setDebouncedSearch]     = useState('');
  const [selectedAssessmentYear, setSelectedAssessmentYear] = useState('');
  const [financialYearFilter, setFinancialYearFilter] = useState('all');
  const [documentStatusFilter, setDocumentStatusFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter]   = useState('all');
  const [showFilters, setShowFilters]             = useState(false);
  const [visibleCount, setVisibleCount]           = useState(PAGE_SIZE);
  const [hasMore, setHasMore]                     = useState(true);
  const loaderRef = useRef(null);
  const observerRef = useRef(null);

  // ── Debounce search input (300ms) ──
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ── Fetch data ──
  const fetchData = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const data = await fetchAllCollections();
      setUserData(data);
      setVisibleCount(PAGE_SIZE);
      setHasMore(data.length > PAGE_SIZE);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
    const id = setInterval(() => fetchData(false), POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  // Helper to resolve the active filing date (Form submission date first, then registration date)
  const getFilingDate = (user) => {
    if (user?.tdsFormData?.timestamp) {
      return new Date(user.tdsFormData.timestamp);
    }
    if (user?.createdAt) {
      return user.createdAt.seconds 
        ? new Date(user.createdAt.seconds * 1000) 
        : new Date(user.createdAt);
    }
    return null;
  };

  // ── Memoized filter ──
  const yearFilteredUsers = useMemo(() => {
    return userData.filter(user => {
      if (financialYearFilter === 'all') {
        return true;
      }

      // A. Show user if they already have TDS details/documents uploaded for this specific year
      const key = `tdsData${financialYearFilter}`;
      if (user.tdsDetails?.[key]?.length) {
        return true;
      }

      // B. If no document is uploaded for this year:
      // Show user only if their form filing date (or signup date) belongs to this selected year!
      const filingDate = getFilingDate(user);
      if (filingDate && isDateInFinancialYear(filingDate, financialYearFilter)) {
        return true;
      }

      return false;
    });
  }, [userData, financialYearFilter]);

  // Helper to resolve PAN from possible locations (user doc or tds_details)
  const getPan = (user) => {
    return (
      user?.pan ||
      user?.tdsDetails?.pan ||
      user?.tdsDetails?.personal?.pan ||
      user?.tdsFormData?.pan ||
      ''
    );
  };

  const filteredUsers = useMemo(() => {
    return yearFilteredUsers.filter(user => {
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        const panVal = getPan(user)?.toLowerCase();
        if (!user.name?.toLowerCase().includes(s) && !panVal?.includes(s)) return false;
      }
      if (documentStatusFilter !== 'all') {
        const isUploaded = !!user.tdsState?.tdsA26s;
        if (documentStatusFilter === 'uploaded' && !isUploaded) return false;
        if (documentStatusFilter === 'incomplete' && isUploaded) return false;
      }
      if (userStatusFilter !== 'all') {
        const isActive = !!user.tdsState?.fileTds;
        if (userStatusFilter === 'active' && !isActive) return false;
        if (userStatusFilter === 'inactive' && isActive) return false;
      }
      return true;
    });
  }, [yearFilteredUsers, debouncedSearch, documentStatusFilter, userStatusFilter]);

  // ── Memoized stats ──
  const stats = useMemo(() => ({
    active:     yearFilteredUsers.filter(u => u.tdsState?.fileTds).length,
    inactive:   yearFilteredUsers.filter(u => !u.tdsState?.fileTds).length,
    uploaded:   yearFilteredUsers.filter(u => u.tdsState?.tdsA26s).length,
    incomplete: yearFilteredUsers.filter(u => !u.tdsState?.tdsA26s).length,
  }), [yearFilteredUsers]);

  // ── Reset visible count when filters change ──
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setHasMore(filteredUsers.length > PAGE_SIZE);
  }, [debouncedSearch, financialYearFilter, documentStatusFilter, userStatusFilter, filteredUsers.length]);

  // ── Load more function ──
  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    
    const nextCount = visibleCount + PAGE_SIZE;
    if (nextCount >= filteredUsers.length) {
      setHasMore(false);
    }
    setVisibleCount(Math.min(nextCount, filteredUsers.length));
    setLoadingMore(false);
  }, [hasMore, loadingMore, visibleCount, filteredUsers.length]);

  // ── Infinite scroll via IntersectionObserver ──
  useEffect(() => {
    if (loading) return;
    
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && visibleCount < filteredUsers.length) {
          setLoadingMore(true);
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' } // Added rootMargin for better trigger
    );

    if (loaderRef.current) {
      observerRef.current.observe(loaderRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, visibleCount, filteredUsers.length, loadMore]);

  // Paginated slice
  const visibleUsers = filteredUsers.slice(0, visibleCount);

  // ── File handler ──
  const handleFileChange = e => setSelectedFile(e.target.files[0]);

  // ── Submit ──
  const handleSubmit = async () => {
    if (!selectedFile || !selectedUser || !selectedAssessmentYear) {
      alert('Please select a file, user, and assessment year.'); 
      return;
    }
    if (additionalFields.some(f => !f.company || !f.income || !f.tds)) {
      alert('Please fill in all required fields.'); 
      return;
    }
    const option = YEAR_OPTIONS.find(o => o.value === selectedAssessmentYear);
    if (!option) { 
      alert('Invalid assessment year.'); 
      return; 
    }

    const fileRef = ref(storage, `tdsdocs/${selectedUser.id}/${option.filename}`);
    try {
      await uploadBytes(fileRef, selectedFile);
      const downloadURL = await getDownloadURL(fileRef);

      const newDocRef = doc(collection(firestore, `tds_docs/${selectedUser.id}/documents`));
      await setDoc(newDocRef, {
        id: newDocRef.id, 
        title: option.filename, 
        downloadUrl: downloadURL,
        storagePath: fileRef.fullPath, 
        assessmentYear: option.label, 
        timestamp: new Date()
      });

      const [tdsDetailsRef, tdsStateRef] = [
        doc(firestore, 'tds_details', selectedUser.id),
        doc(firestore, 'tds_state',   selectedUser.id)
      ];
      await Promise.all([
        setDoc(tdsDetailsRef, { [option.firestoreKey]: arrayUnion(...additionalFields) }, { merge: true }),
        updateDoc(tdsStateRef, { tdsDetails: true, tdsA26s: true })
      ]);

      // Auto-send notification when document is successfully uploaded
      sendNotification(selectedUser);

      alert('Data updated successfully!');
      setDialogOpen(false);
      clearFormData();
      fetchData(false);
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file. Please try again.');
    }
  };

  const handleAddMoreFields    = () => setAdditionalFields(f => [...f, { company: '', income: '', tds: '' }]);
  const handleFieldChange      = (i, field, val) => setAdditionalFields(f => { const n=[...f]; n[i][field]=val; return n; });
  const handleRemoveFields     = i  => setAdditionalFields(f => f.length > 1 ? f.filter((_, idx) => idx !== i) : f);

  const sendNotification = async (user) => {
    try {
      await fetch('https://moneycartadmin.onrender.com/send-notification3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          message: `Your Form 26 AS has been uploaded successfully. You can now view your TDS refund amount under the 'Documents' section`
        })
      });
    } catch (err) { 
      console.error('Notification error:', err); 
    }
  };

  const handleStatusChange = async (user, status) => {
    const bool = status === 'true';
    try {
      await updateDoc(doc(firestore, 'tds_state', user.id), { tdsA26s: bool });
      setUserData(prev => prev.map(u =>
        u.id === user.id ? { ...u, tdsState: { ...u.tdsState, tdsA26s: bool } } : u
      ));
      if (bool) sendNotification(user);
    } catch (err) { 
      console.error('Status update error:', err); 
    }
  };

  const clearFormData = () => {
    setSelectedAssessmentYear('');
    setSelectedFile(null);
    setAdditionalFields([{ company: '', income: '', tds: '' }]);
  };

  const handleDownloadReport = async (user) => {
    try {
      const snap = await getDoc(doc(firestore, 'tds_details', user.id));
      const details = snap.exists() ? snap.data() : {};

      let csv = "Name,PAN,Company,Income,TDS\n";
      csv += `${user.name},${getPan(user) || ''}\n`;
      YEAR_OPTIONS.forEach(opt => {
        const rows = details[opt.firestoreKey];
        if (rows?.length) {
          csv += `\nAssessment Year: ${opt.label}\n`;
          rows.forEach(r => { csv += `,,${r.company},${r.income},${r.tds}\n`; });
        }
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${user.name}_TDS_Report.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download report.');
    }
  };

  const handleRowClick    = user => { setSelectedUser(user); clearFormData(); setDialogOpen(true); };
  const handleDialogClose = ()   => { setDialogOpen(false); clearFormData(); };

  const renderTdsFields = () => additionalFields.map((field, i) => (
    <div key={i} style={{ marginBottom: 20, borderBottom: '1px solid #ccc', paddingBottom: 10 }}>
      {['company','income','tds'].map(key => (
        <TextField 
          key={key} 
          label={key.charAt(0).toUpperCase() + key.slice(1)} 
          variant="outlined"
          fullWidth 
          margin="normal" 
          value={field[key]} 
          required
          onChange={e => handleFieldChange(i, key, e.target.value)} 
        />
      ))}
      {additionalFields.length > 1 && (
        <Button onClick={() => handleRemoveFields(i)} variant="contained" color="secondary" sx={{ mt: 1 }}>
          Remove
        </Button>
      )}
    </div>
  ));

  return (
    <div>
      <NavbarDashboard />
      <Box height={10} />
      <Box sx={{ display: 'flex' }}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box component="main" sx={{ flexGrow: 1, p: 9 }}>
          <div className="container">
            <div className="card p-4">
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" gutterBottom>TDS Details</Typography>
                <Button variant="outlined" startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(v => !v)}>
                  Filters
                </Button>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                  <TextField 
                    label="Search by Name or PAN" 
                    variant="outlined" 
                    fullWidth
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                  />
                </Grid>
                {showFilters && (
                  <>
                    {[
                      { 
                        label: 'Assessment Year', 
                        val: financialYearFilter, 
                        set: setFinancialYearFilter,
                        items: [{ v:'all', l:'All Years' }, ...YEAR_OPTIONS.map(o => ({ v: o.value, l: o.label }))] 
                      },
                      { 
                        label: 'Document Status', 
                        val: documentStatusFilter, 
                        set: setDocumentStatusFilter,
                        items: [{ v:'all', l:'All Documents' }, { v:'uploaded', l:'Uploaded' }, { v:'incomplete', l:'Incomplete' }] 
                      },
                      { 
                        label: 'User Status', 
                        val: userStatusFilter, 
                        set: setUserStatusFilter,
                        items: [{ v:'all', l:'All Users' }, { v:'active', l:'Active' }, { v:'inactive', l:'Inactive' }] 
                      }
                    ].map(({ label, val, set, items }) => (
                      <Grid key={label} item xs={12} md={2}>
                        <FormControl fullWidth>
                          <InputLabel>{label}</InputLabel>
                          <Select value={val} label={label} onChange={e => set(e.target.value)}>
                            {items.map(i => <MenuItem key={i.v} value={i.v}>{i.l}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip 
                  label={`Active Users: ${stats.active}`} 
                  color="success" 
                  clickable
                  variant={userStatusFilter === 'active' ? 'filled' : 'outlined'}
                  onClick={() => setUserStatusFilter(v => v === 'active' ? 'all' : 'active')} 
                />
                <Chip 
                  label={`Inactive Users: ${stats.inactive}`} 
                  color="error" 
                  clickable
                  variant={userStatusFilter === 'inactive' ? 'filled' : 'outlined'}
                  onClick={() => setUserStatusFilter(v => v === 'inactive' ? 'all' : 'inactive')} 
                />
                <Chip 
                  label={`Uploaded Docs: ${stats.uploaded}`} 
                  color="success" 
                  clickable
                  variant={documentStatusFilter === 'uploaded' ? 'filled' : 'outlined'}
                  onClick={() => setDocumentStatusFilter(v => v === 'uploaded' ? 'all' : 'uploaded')} 
                />
                <Chip 
                  label={`Incomplete: ${stats.incomplete}`} 
                  color="warning" 
                  clickable
                  variant={documentStatusFilter === 'incomplete' ? 'filled' : 'outlined'}
                  onClick={() => setDocumentStatusFilter(v => v === 'incomplete' ? 'all' : 'incomplete')} 
                />
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Date & Time','Name','PAN','26As Status','Upload Details','26As Form Upload','Download Report']
                        .map(h => <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                    ) : (
                      <>
                        {visibleUsers.map(user => (
                          <TableRow key={user.id} hover>
                            <TableCell>
                              {user.tdsFormData.timestamp
                                ? new Date(user.tdsFormData.timestamp).toLocaleString()
                                : 'NA'}
                            </TableCell>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{getPan(user) || 'NA'}</TableCell>
                            <TableCell className={user.tdsState?.fileTds ? 'Active' : 'Inactive'}>
                              {user.tdsState?.fileTds ? 'Active' : 'Inactive'}
                            </TableCell>
                            <TableCell>
                              <Button variant="contained" color="primary" onClick={() => handleRowClick(user)} size="small">
                                Add TDS Details
                              </Button>
                            </TableCell>
                            <TableCell className={user.tdsState?.tdsA26s ? 'Uploaded' : 'Incomplete'}>
                              <Select 
                                style={{ color: 'white' }}
                                size="small"
                                value={user.tdsState?.tdsA26s ? 'true' : 'false'}
                                onChange={e => handleStatusChange(user, e.target.value)}
                              >
                                <MenuItem value="true">Uploaded</MenuItem>
                                <MenuItem value="false">Incomplete</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleDownloadReport(user)} 
                                title="Download TDS Report"
                                size="small"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Loading indicator for more data */}
              {!loading && hasMore && visibleCount < filteredUsers.length && (
                <Box ref={loaderRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={30} />
                  <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                    Loading more...
                  </Typography>
                </Box>
              )}

              {/* End of list message */}
              {!loading && !hasMore && filteredUsers.length > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    ✓ All {filteredUsers.length} users loaded
                  </Typography>
                </Box>
              )}

              {!loading && filteredUsers.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="textSecondary">
                    No users match the current filters
                  </Typography>
                </Box>
              )}

              <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>Add TDS Details for {selectedUser?.name}</DialogTitle>
                <DialogContent>
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel>Assessment Year</InputLabel>
                    <Select 
                      value={selectedAssessmentYear}
                      onChange={e => setSelectedAssessmentYear(e.target.value)} 
                      label="Assessment Year"
                    >
                      {YEAR_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField 
                    type="file" 
                    onChange={handleFileChange} 
                    margin="normal"
                    fullWidth 
                    InputLabelProps={{ shrink: true }} 
                    required 
                  />
                  {renderTdsFields()}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDialogClose} color="secondary">Cancel</Button>
                  <Button onClick={handleSubmit} color="primary" variant="contained">Submit</Button>
                </DialogActions>
              </Dialog>
            </div>
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default TdsDetails;

const css = `
  .Incomplete, .Inactive { background-color: #EF0107; color: white; border-style: solid; }
  .Uploaded, .Active     { background-color: #03C03C; color: white; border-style: solid; }
  .MuiTableContainer-root { overflow: auto; }
  .MuiTable-stickyHeader  { position: sticky; top: 0; z-index: 10; }
  .MuiTableHead-root      { background-color: lightgreen; }
  .MuiTableHead-root th   { background-color: lightgreen; font-weight: bold; position: sticky; top: 0; z-index: 11; }
  
  /* Hover effect */
  .MuiTableRow-root:hover {
    background-color: #f5f5f5;
  }
`;
document.head.insertAdjacentHTML('beforeend', `<style>${css}</style>`);
